import { Injectable } from '@angular/core';
import { isDefined } from '@freelancer/utils';
import type { Observable } from 'rxjs';
import { Subject } from 'rxjs';
import { auditTime } from 'rxjs/operators';
import type { StickyContext, StickyElement } from './sticky.interface';

@Injectable({ providedIn: 'root' })
export class StickyService {
  private activeTop: StickyElement[] = [];
  private activeBottom: StickyElement[] = [];
  private _topStackHeightSubject$ = new Subject<void>();
  private _bottomStackHeightSubject$ = new Subject<void>();

  add({
    element,
    isStickyTop,
    order,
    context = 'global',
  }: {
    element: HTMLElement;
    isStickyTop: boolean;
    order?: number;
    context?: StickyContext;
  }): void {
    if (isStickyTop) {
      this.activeTop = this.addActiveElement({
        activeElements: this.activeTop,
        element,
        order,
        context,
      });
    } else {
      this.activeBottom = this.addActiveElement({
        activeElements: this.activeBottom,
        element,
        order,
        context,
      });
    }
  }

  remove(element: HTMLElement, isStickyTop: boolean): void {
    if (isStickyTop) {
      this.activeTop = this.activeTop.filter(item => item.element !== element);
    } else {
      this.activeBottom = this.activeBottom.filter(
        item => item.element !== element,
      );
    }
  }

  getOffset({
    target,
    isStickyTop,
    context = 'global',
  }: {
    target: HTMLElement;
    isStickyTop: boolean;
    context?: StickyContext;
  }): number {
    const activeElements = isStickyTop ? this.activeTop : this.activeBottom;
    let offset = 0;
    let index;

    for (index = 0; index < activeElements.length; index++) {
      const item = activeElements[index];

      // Only consider elements in the same context
      if (item.context === context) {
        if (item.element === target) {
          return offset;
        }

        offset += item.element.offsetHeight;
      }
    }

    return offset;
  }

  isTopmost({
    element,
    isStickyTop,
    context = 'global',
  }: {
    element: HTMLElement;
    isStickyTop: boolean;
    context?: StickyContext;
  }): boolean {
    if (!isStickyTop) {
      return false;
    }

    const firstInContext = this.activeTop.find(
      item => item.context === context,
    );
    return firstInContext ? firstInContext.element === element : false;
  }

  stackHeightChange(isStickyTop: boolean): Observable<void> {
    return (
      isStickyTop
        ? this._topStackHeightSubject$.asObservable()
        : this._bottomStackHeightSubject$.asObservable()
    ).pipe(auditTime(0));
  }

  /**
   * Notify that stack heights have changed
   * This allows elements to recalculate their offsets
   */
  notifyStackHeightChange(isStickyTop: boolean): void {
    if (isStickyTop) {
      this._topStackHeightSubject$.next();
    } else {
      this._bottomStackHeightSubject$.next();
    }
  }

  /**
   * Inserts an element based on the following rules:
   * 1. If order is not specified, it's inserted at the end of the list with the highest order in the list + 1 (or 0 if the list is empty)
   * 2. If order is specified, then it will be inserted before the first element with order greater than it or at the end of the list otherwise.
   */
  private addActiveElement({
    activeElements,
    element,
    order,
    context = 'global',
  }: {
    activeElements: StickyElement[];
    element: HTMLElement;
    order?: number;
    context?: StickyContext;
  }): StickyElement[] {
    if (activeElements.length === 0) {
      return [{ element, context, order: order ?? 0 }];
    }

    // highest order should exist given activeElements.length > 0
    const highestOrder = Number(activeElements.at(-1)?.order);

    if (isDefined(order) && order >= highestOrder) {
      return [...activeElements, { element, context, order }];
    }

    if (isDefined(order) && order < highestOrder) {
      const firstHigherOrderIndex = activeElements.findIndex(
        item => item.order > order,
      );
      activeElements.splice(firstHigherOrderIndex, 0, {
        element,
        context,
        order,
      });

      return activeElements;
    }

    // order should be undefined at this point
    return [...activeElements, { element, context, order: highestOrder + 1 }];
  }
}
